/**
 * VerificationRequestManager - Handles incoming device verification requests
 * 
 * Key Features:
 * - Listens for incoming verification requests
 * - Checks if current device is verified before showing toast (CRITICAL!)
 * - Manages toast notifications for verification requests
 * - Tracks active requests
 * - Handles timeouts
 * - Coordinates toast → modal handoff
 */

import deviceTrustManager from '../crypto/DeviceTrustManager';
import logger from '../utils/logger';
import { resolveDeviceName } from '../utils/resolveDeviceName';

const VERIFICATION_TIMEOUT = 5 * 60 * 1000; // 5 minutes

// Identity-translate used before i18n is wired and after the React tree unmounts.
const IDENTITY_TRANSLATE = (key, _vars, defaultValue) => defaultValue || key;

class VerificationRequestManager {
  constructor() {
    this.activeRequests = new Map(); // requestId -> request object
    this.requestListeners = new Map(); // requestId -> change listener (for cleanup)
    this.toastContext = null; // Will be set by initialization
    this.modalContext = null; // Will be set by initialization
    this.matrixService = null; // Will be set by initialization
    // Identity-translate fallback so the manager works even before i18n is wired.
    this.t = IDENTITY_TRANSLATE;
  }

  /**
   * Initialize manager with required contexts
   * Should be called after app contexts are available
   */
  initialize(toastContext, modalContext, matrixService, t) {
    this.toastContext = toastContext;
    this.modalContext = modalContext;
    this.matrixService = matrixService;
    this.setTranslator(t);
    logger.debug('VerificationRequestManager: Initialized');
  }

  /**
   * Set (or clear) the i18n translate function used for user-facing strings.
   * Passing null/undefined restores the identity fallback — important on React
   * unmount so the singleton does not retain a closure over a torn-down tree.
   *
   * @param {Function|null} t - translate fn (key, vars, defaultValue) => string
   */
  setTranslator(t) {
    this.t = typeof t === 'function' ? t : IDENTITY_TRANSLATE;
  }

  /**
   * Handle incoming verification request
   * This is the CRITICAL method that implements the "only show toast on verified devices" logic
   * 
   * @param {Object} request - Matrix verification request object
   */
  async handleIncomingRequest(request) {
    if (!request) {
      logger.warn('VerificationRequestManager: No request provided');
      return;
    }

    const requestId = request.transactionId || request.channel?.transactionId;
    if (!requestId) {
      logger.warn('VerificationRequestManager: No transaction ID in request');
      return;
    }

    logger.debug('VerificationRequestManager: Received verification request:', requestId);

    // CRITICAL: Check if current device is verified before showing toast
    const isCurrentDeviceVerified = await deviceTrustManager.isCurrentDeviceVerified();

    if (!isCurrentDeviceVerified) {
      logger.debug('VerificationRequestManager: Ignoring request - current device is not verified');
      return; // Don't show toast on unverified devices
    }

    // Check if request is from same user (device verification)
    const client = this.matrixService?.client;
    if (!client) {
      logger.warn('VerificationRequestManager: No Matrix client available');
      return;
    }

    const currentUserId = client.getUserId();
    const requestUserId = request.otherUserId || request.userId;

    if (requestUserId !== currentUserId) {
      // This is a user verification request, not device verification
      // For now, we only handle device verification via toast
      logger.debug('VerificationRequestManager: Request is for user verification, not device');
      return;
    }

    // Check if we already have this request
    if (this.activeRequests.has(requestId)) {
      logger.debug('VerificationRequestManager: Request already being handled:', requestId);
      return;
    }

    // Filter out requests initiated by this device (self-verification that we started)
    // The initiatedByMe property is set by Matrix SDK when this device sent the request
    if (request.initiatedByMe) {
      logger.debug('VerificationRequestManager: Ignoring request initiated by this device (initiatedByMe=true)');
      return;
    }

    // Fallback: also check device ID in case initiatedByMe is not available
    const currentDeviceId = client.getDeviceId();
    const requestingDeviceId = request.otherDeviceId || request.deviceId;

    if (requestingDeviceId && requestingDeviceId === currentDeviceId) {
      logger.debug('VerificationRequestManager: Ignoring self-verification request from own device');
      return;
    }

    // Store the request
    this.activeRequests.set(requestId, request);

    // Get device name for the requesting device
    const deviceName = await this.getDeviceName(request);

    // FIX: Register listener BEFORE showing toast to avoid race condition
    // Listen for remote cancellation to dismiss toast immediately
    // When the initiator cancels, the request phase changes to 5 (cancelled)
    const changeListener = () => {
      const phase = request.phase;
      // Phase 5 = Cancelled in Rust crypto, 'cancelled' in legacy
      if (phase === 5 || phase === 'cancelled') {
        logger.info('VerificationRequestManager: Request cancelled by remote, dismissing toast:', requestId);
        this.handleRemoteCancellation(requestId);
      }
    };

    // Register listener
    request.on?.('change', changeListener);
    this.requestListeners.set(requestId, changeListener);

    // Show toast notification
    if (!this.toastContext) {
      logger.warn('VerificationRequestManager: Toast context not initialized');
      return;
    }

    this.toastContext.showVerificationRequest({
      requestId,
      deviceName,
      onVerify: () => this.handleVerifyClick(request, requestId),
      onIgnore: () => this.handleIgnoreClick(requestId),
      timeout: VERIFICATION_TIMEOUT,
    });

    logger.info(`Received verification request from device: ${deviceName}`);
  }

  /**
   * Get display name for the device making the request.
   * Delegates to the shared resolver so the toast and modal labels stay in sync.
   */
  async getDeviceName(request) {
    return resolveDeviceName(request, {
      client: this.matrixService?.client,
      t: this.t,
    });
  }

  /**
   * Handle user clicking "Verify" in toast
   * Opens verification modal - actual modal rendering should be handled by app component
   */
  handleVerifyClick(request, requestId) {
    logger.info('User clicked Verify button for request:', requestId);

    // Clean up the change listener since user is handling the request manually
    // The modal will take over from here
    this.cleanupRequestListener(requestId);

    // Emit event for modal to open
    // The app component will listen for this and open the modal
    if (this.onVerifyRequest) {
      try {
        const result = this.onVerifyRequest(request, requestId);
        if (result && result.then) {
          result.catch((err) => {
            logger.error('Error in onVerifyRequest callback:', err);
          });
        }
      } catch (err) {
        logger.error('Error executing onVerifyRequest callback:', err);
      }
    } else {
      logger.warn('VerificationRequestManager: No onVerifyRequest callback set');
    }
  }

  /**
   * Set callback for when user clicks Verify
   * @param {Function} callback - Function(request, requestId)
   */
  setOnVerifyRequest(callback) {
    this.onVerifyRequest = callback;
  }

  /**
   * Handle user clicking "Ignore" in toast
   */
  handleIgnoreClick(requestId) {
    logger.debug('VerificationRequestManager: User clicked Ignore:', requestId);

    // Clean up listener before removing request
    this.cleanupRequestListener(requestId);

    // Just remove from active requests, don't cancel the request server-side
    // The request will timeout naturally
    this.activeRequests.delete(requestId);
  }

  /**
   * Handle remote cancellation - dismiss toast and clean up
   * Called when the initiator cancels the verification request
   */
  handleRemoteCancellation(requestId) {
    // FIX: Add idempotency guard to prevent multiple calls from multiple events
    if (!this.activeRequests.has(requestId)) {
      logger.debug('VerificationRequestManager: Request already handled:', requestId);
      return;
    }

    logger.info('VerificationRequestManager: Handling remote cancellation:', requestId);

    // Dismiss the toast immediately
    if (this.toastContext?.dismissToast) {
      this.toastContext.dismissToast(requestId);
    }

    // Clean up listener and request
    this.cleanupRequestListener(requestId);
    this.activeRequests.delete(requestId);
  }

  /**
   * Clean up the change listener for a request
   */
  cleanupRequestListener(requestId) {
    const listener = this.requestListeners.get(requestId);
    const request = this.activeRequests.get(requestId);

    if (listener && request) {
      try {
        request.off?.('change', listener);
      } catch (err) {
        logger.warn('VerificationRequestManager: Error removing listener:', err);
      }
    }

    this.requestListeners.delete(requestId);
  }

  /**
   * Handle request timeout
   */
  handleTimeout(requestId) {
    logger.debug('VerificationRequestManager: Request timeout:', requestId);

    // Dismiss the toast explicitly for consistency with handleRemoteCancellation
    if (this.toastContext?.dismissToast) {
      this.toastContext.dismissToast(requestId);
    }

    // Clean up listener before removing request
    this.cleanupRequestListener(requestId);

    const request = this.activeRequests.get(requestId);
    if (request) {
      try {
        // Cancel the request server-side
        request.cancel?.('timeout');
      } catch (error) {
        logger.warn('VerificationRequestManager: Error cancelling request:', error);
      }

      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Clear all active requests
   */
  clearAll() {
    // Clean up all listeners
    for (const requestId of this.requestListeners.keys()) {
      this.cleanupRequestListener(requestId);
    }
    this.activeRequests.clear();
    this.requestListeners.clear();
  }

  /**
   * Get number of active requests
   */
  getActiveRequestCount() {
    return this.activeRequests.size;
  }
}

// Create singleton instance
const verificationRequestManager = new VerificationRequestManager();
export default verificationRequestManager;

// Also export class for testing
export { VerificationRequestManager };
